import { colors } from 'theme/colors';

/**
 * The risk band ladder for the Risk Register, as a share of the maximum score so it holds at
 * any matrix size. On a five by five: Very low 1-2, Low 3-4, Medium 5-9, High 10-12, Very high
 * 15-16, Extreme 20-25.
 *
 * The Risk Assessments module and the document editor's risk nodes band through a separate
 * two-armed function in containers/RiskAssessmentsDetails, which disagrees at 4, 15, 16 and
 * 20-25 and never reaches Extreme. Reconcile the two when that module is removed, which is
 * what deletes the other function.
 */
export enum RiskBand {
  VeryLow = 'Very low',
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  VeryHigh = 'Very high',
  Extreme = 'Extreme',
}

export const DEFAULT_MATRIX_SIZE = 5;

// Upper bound of each band as a percentage of the maximum score, in order.
// Extreme is everything above the last threshold, which is why it has no entry.
const BAND_THRESHOLDS: ReadonlyArray<[number, RiskBand]> = [
  [8, RiskBand.VeryLow],
  [16, RiskBand.Low],
  [36, RiskBand.Medium],
  [56, RiskBand.High],
  [76, RiskBand.VeryHigh],
];

// The two green tokens are named for the bands they served before this ladder existed:
// `low` is the bright green of Very low and `lowAlt` the dark green of Low. Renaming them
// is a tidy-up for whoever removes the Risk Assessments module, not work for this ticket.
export const BAND_COLORS: Record<RiskBand, { bg: string; color: string }> = {
  [RiskBand.VeryLow]: { bg: colors.ui.risk.low, color: colors.common.white },
  [RiskBand.Low]: { bg: colors.ui.risk.lowAlt, color: colors.common.white },
  [RiskBand.Medium]: { bg: colors.ui.risk.medium, color: colors.common.black },
  [RiskBand.High]: { bg: colors.ui.risk.high, color: colors.common.black },
  [RiskBand.VeryHigh]: { bg: colors.ui.risk.veryHigh, color: colors.common.white },
  [RiskBand.Extreme]: { bg: colors.ui.risk.extreme, color: colors.common.white },
};

export const getRiskBand = (
  severity: number,
  likelihood: number,
  severityLevels: number = DEFAULT_MATRIX_SIZE,
  likelihoodLevels: number = DEFAULT_MATRIX_SIZE
): RiskBand => {
  const percentage = ((severity * likelihood) / (severityLevels * likelihoodLevels)) * 100;
  const match = BAND_THRESHOLDS.find(([upperBound]) => percentage <= upperBound);
  return match ? match[1] : RiskBand.Extreme;
};

export const getBandColor = (band: RiskBand): { bg: string; color: string } => BAND_COLORS[band];
